import React from "react";
import { CirclePlus, X } from "lucide-react";
import { Input, ValidationIndicator } from "@/components/atoms";
import type { ValidationResult } from "@/hooks/useFieldValidation";
import { cn } from "@/utils";

interface MultiEmailInputProps {
  /** Массив значений email */
  values: string[];
  /** Callback при изменении значения по индексу */
  onChange: (index: number, value: string) => void;
  /** Callback для добавления нового элемента */
  onAdd: () => void;
  /** Callback для удаления элемента по индексу */
  onRemove: (index: number) => void;
  /** Callback для валидации по blur (принимает значение и fieldKey) */
  onBlurValidate?: (value: string, fieldKey: string) => void;
  /** Объект с результатами валидации по ключам (email_0, email_1 и т.д.) */
  validations: Record<string, ValidationResult | undefined>;
  /** Индексы свёрнутых элементов (не отображаются) */
  collapsedIndices?: number[];
  /** Максимальное количество элементов */
  maxItems?: number;
  /** Дополнительные CSS классы */
  className?: string;
  /** Placeholder для полей */
  placeholder?: string;
  /** Callback для регистрации рефов к инпутам (index, ref) */
  onRegisterRef?: (index: number, ref: HTMLInputElement | null) => void;
}

/**
 * Компонент для множественного ввода email-адресов.
 * Поддерживает добавление до maxItems элементов с независимой валидацией.
 */
const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
  values,
  onChange,
  onAdd,
  onRemove,
  onBlurValidate,
  validations,
  collapsedIndices = [],
  maxItems = 5,
  className,
  placeholder = "example@mail.com",
  onRegisterRef,
}) => {
  const canAdd = values.length < maxItems;

  // Фильтруем индексы, показывая только не свёрнутые элементы
  const visibleIndices = values.map((_, index) => index).filter((index) => !collapsedIndices.includes(index));

  const getFieldKey = (index: number) => `email_${index}`;

  const handleBlur = (index: number, value: string) => {
    if (onBlurValidate && value && value.includes("@")) {
      const fieldKey = getFieldKey(index);
      onBlurValidate(value, fieldKey);
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      {visibleIndices.map((index) => {
        const value = values[index];
        const fieldKey = getFieldKey(index);
        const validation = validations[fieldKey];
        const showValidationError = validation && validation.status !== "valid";

        return (
          <div key={index} className="relative">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Input
                  ref={(el) => onRegisterRef?.(index, el)}
                  value={value}
                  onChange={(e) => onChange(index, e.target.value)}
                  onBlur={() => handleBlur(index, value)}
                  placeholder={placeholder}
                  type="email"
                />
              </div>
              {/* Кнопка удаления (показываем если есть видимые элементы > 1) */}
              {visibleIndices.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className={cn(
                    "flex-shrink-0 p-2 rounded-lg transition-colors",
                    "text-ink-3 hover:text-error-500 hover:bg-error-50",
                    "dark:text-gray-500 dark:hover:text-error-400 dark:hover:bg-error-900/20",
                  )}
                  title="Удалить"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            {/* Индикатор валидации */}
            {showValidationError && (
              <ValidationIndicator
                status={validation.status}
                message={validation.message}
                details={validation.details}
                className="mt-1"
              />
            )}
          </div>
        );
      })}

      {/* Кнопка добавления */}
      {canAdd && (
        <button
          type="button"
          onClick={onAdd}
          className={cn(
            "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-pill",
            "border border-dashed border-line-strong bg-transparent text-fs-xs font-medium text-ink-1",
            "hover:bg-surface-hover hover:border-line transition-colors",
          )}
        >
          <CirclePlus size={13} strokeWidth={2} className="text-ink-2 shrink-0" />
          Добавить e-mail
        </button>
      )}
    </div>
  );
};

export default MultiEmailInput;
